import { ROAD_VEHICLE_EMERGENCY_YIELD_TUNING } from "../../config/vehicles.ts";
import type { ResidentPresentation } from "../neighborhood/index.ts";
import type { RoadVehiclePresentation } from "./index.ts";

/**
 * Existing M5 residents make a small deterministic step aside for an active
 * response vehicle. NPC road traffic remains outside the current milestone.
 */
export function yieldResidentsToEmergencyVehicles(
  residents: readonly Readonly<ResidentPresentation>[],
  vehicles: readonly Readonly<RoadVehiclePresentation>[],
): readonly ResidentPresentation[] {
  const activeVehicles = vehicles.filter((vehicle) =>
    vehicle.beaconOn || vehicle.sirenOn
  );
  if (activeVehicles.length === 0) return residents.map(copyResident);

  return residents.map((resident) => {
    const nearest = activeVehicles
      .map((vehicle) => ({
        vehicle,
        deltaX: resident.position.x - vehicle.position.x,
        deltaZ: resident.position.z - vehicle.position.z,
        distanceM: Math.hypot(
          resident.position.x - vehicle.position.x,
          resident.position.z - vehicle.position.z,
        ),
      }))
      .filter(({ distanceM }) =>
        distanceM < ROAD_VEHICLE_EMERGENCY_YIELD_TUNING.residentYieldRadiusM
      )
      .sort((left, right) => left.distanceM - right.distanceM)[0];
    if (nearest === undefined) return copyResident(resident);

    const fallbackX = Math.cos(nearest.vehicle.yawRadians);
    const fallbackZ = -Math.sin(nearest.vehicle.yawRadians);
    const inverseDistance = nearest.distanceM > 0.05
      ? 1 / nearest.distanceM
      : 0;
    const awayX = inverseDistance === 0
      ? fallbackX
      : nearest.deltaX * inverseDistance;
    const awayZ = inverseDistance === 0
      ? fallbackZ
      : nearest.deltaZ * inverseDistance;
    const proximity01 = 1 - nearest.distanceM /
      ROAD_VEHICLE_EMERGENCY_YIELD_TUNING.residentYieldRadiusM;
    const offsetM = proximity01 * proximity01 *
      ROAD_VEHICLE_EMERGENCY_YIELD_TUNING.maximumResidentStepAsideM;
    return {
      ...resident,
      position: {
        ...resident.position,
        x: resident.position.x + awayX * offsetM,
        z: resident.position.z + awayZ * offsetM,
      },
      yawRadians: Math.atan2(-awayX, -awayZ),
    };
  });
}

function copyResident(
  resident: Readonly<ResidentPresentation>,
): ResidentPresentation {
  return { ...resident, position: { ...resident.position } };
}
